import axios from 'axios';
import type {
  DivisionRankingsResponse,
  HealthResponse,
  RefreshRequest,
  RefreshResponse,
  ApiError,
  CSRTimelineResponse,
  DivisionInfo,
  DivisionPeriodStatsResponse,
  ScheduleResponse,
  SessionScorecardResponse,
  CompSheetResponse,
  AnalysisResponse,
  AnalyzeRequest,
  OptimalRosterResponse,
  WinProbModel,
  HeadToHeadResponse,
  SeasonRankingsResponse,
  SeasonInfo,
} from '@/types/api';

export const isWriteEnabled = String(import.meta.env.VITE_ENABLE_WRITE_ENDPOINTS ?? 'true').toLowerCase() !== 'false';

// Create axios instance with default config
// In production with separate API container, use VITE_API_BASE_URL; locally, use relative /api
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth token getter — set by AuthProvider when available
let _getAccessToken: (() => Promise<string | null>) | null = null;

export const setAuthTokenGetter = (getter: () => Promise<string | null>) => {
  _getAccessToken = getter;
};

// Request interceptor — attach Bearer token on write requests
api.interceptors.request.use(
  async (config) => {
    if (import.meta.env?.DEV) {
      console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`); // debug
    }

    // Attach Bearer token for write operations (POST, DELETE, PUT, PATCH)
    const method = config.method?.toUpperCase();
    const isWrite = method && ['POST', 'DELETE', 'PUT', 'PATCH'].includes(method);
    // Exclude read-only POST endpoints (matchup analysis)
    const isReadOnlyPost = method === 'POST' && (
      config.url?.includes('/matchup/analyze') ||
      config.url?.includes('/matchup/optimal-roster')
    );

    if (isWrite && !isReadOnlyPost && _getAccessToken) {
      try {
        const token = await _getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch {
        // Token acquisition may fail silently — middleware will return 401
      }
    }

    return config;
  },
  (error) => {
    if (import.meta.env?.DEV) {
      console.error('API Request Error:', error); // debug
    }
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    if (import.meta.env?.DEV) {
      console.log(`API Response: ${response.status} ${response.config.url}`); // debug
    }
    return response;
  },
  (error) => {
    if (import.meta.env?.DEV) {
      console.error('API Response Error:', error); // debug
    }
    
    // Handle network errors
    if (!error.response) {
      throw new Error('Network error - please check your connection');
    }

    // Handle API errors
    const apiError: ApiError = error.response.data;
    throw new Error(apiError.detail || 'An unexpected error occurred');
  }
);

// API service functions
export const apiService = {
  // Health check
  async checkHealth(): Promise<HealthResponse> {
    try {
      const response = await api.get<HealthResponse>('/health');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get division rankings (unsorted - we'll sort on frontend)
  async getDivisionRankings(
    divisionId: string,
    groupByTeam: boolean = false
  ): Promise<DivisionRankingsResponse> {
    try {
      const params = new URLSearchParams({
        group_by_team: groupByTeam.toString(),
      });

      const response = await api.get<DivisionRankingsResponse>(
        `/divisions/${divisionId}/rankings?${params}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Refresh division data (scrapes from poolshooters.com — can take several minutes)
  async refreshDivision(
    divisionId: string,
    options: RefreshRequest = {}
  ): Promise<RefreshResponse> {
    try {
      const response = await api.post<RefreshResponse>(
        `/divisions/${divisionId}/refresh`,
        options,
        { timeout: 5 * 60 * 1000 } // 5 minute timeout for scraping
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get player profile URL
  getPlayerProfileUrl(playerId: string): string {
    return `https://poolshooters.com/stats.php?playerID=${playerId}`;
  },

  async getCSRTimeline(
    playerId: string,
    gameType: string,
    days?: number,
    divisionId?: string
  ): Promise<CSRTimelineResponse> {
    const params = new URLSearchParams({ game_type: gameType });
    if (days) params.set('days', String(days));
    if (divisionId) params.set('division_id', divisionId);
    const response = await api.get<CSRTimelineResponse>(
      `/history/matches/${playerId}/csr-timeline?${params}`
    );
    return response.data;
  },

  // Get list of all divisions with metadata
  async getDivisions(): Promise<DivisionInfo[]> {
    const response = await api.get<{ divisions: DivisionInfo[]; total: number }>('/divisions');
    return response.data.divisions;
  },

  // Get per-player CSR values for a specific division/season period
  async getDivisionPeriodStats(divisionId: string): Promise<DivisionPeriodStatsResponse> {
    const response = await api.get<DivisionPeriodStatsResponse>(
      `/divisions/${divisionId}/period-stats`
    );
    return response.data;
  },

  // Matchup optimizer endpoints
  async getSchedule(divisionId: string): Promise<ScheduleResponse> {
    const response = await api.get<ScheduleResponse>(`/matchup/schedule/${divisionId}`);
    return response.data;
  },

  async getSessionScorecard(divisionId: string, week: number, homeTeam?: string, awayTeam?: string): Promise<SessionScorecardResponse> {
    const params: Record<string, string | number> = { division_id: divisionId, week };
    if (homeTeam) params.home_team = homeTeam;
    if (awayTeam) params.away_team = awayTeam;
    const response = await api.get<SessionScorecardResponse>('/matchup/session-scorecard', { params });
    return response.data;
  },

  async getCompSheet(
    divisionId: string,
    homeTeamId: string,
    awayTeamId: string,
    weekNumber?: number,
    weekDay?: string,
  ): Promise<CompSheetResponse> {
    const params = new URLSearchParams({
      division_id: divisionId,
      home_team_id: homeTeamId,
      away_team_id: awayTeamId,
    });
    if (weekNumber) params.set('week_number', String(weekNumber));
    if (weekDay) params.set('week_day', weekDay);
    const response = await api.get<CompSheetResponse>(`/matchup/comp-sheet?${params}`);
    return response.data;
  },

  async analyzeMatchup(request: AnalyzeRequest): Promise<AnalysisResponse> {
    const response = await api.post<AnalysisResponse>('/matchup/analyze', request);
    return response.data;
  },

  async getOptimalRoster(
    divisionId: string,
    homeTeamId: string,
    awayTeamId: string,
    userTeam: 'home' | 'away',
    weekNumber?: number,
    weekDay?: string,
  ): Promise<OptimalRosterResponse> {
    const params = new URLSearchParams({
      division_id: divisionId,
      home_team_id: homeTeamId,
      away_team_id: awayTeamId,
      user_team: userTeam,
    });
    if (weekNumber) params.set('week_number', String(weekNumber));
    if (weekDay) params.set('week_day', weekDay);
    const response = await api.post<OptimalRosterResponse>(`/matchup/optimal-roster?${params}`);
    return response.data;
  },

  async getProbabilityMatrix(
    divisionId: string,
    homeTeamId: string,
    awayTeamId: string,
    weekNumber?: number,
    weekDay?: string,
  ): Promise<WinProbModel[]> {
    const params = new URLSearchParams({
      division_id: divisionId,
      home_team_id: homeTeamId,
      away_team_id: awayTeamId,
    });
    if (weekNumber) params.set('week_number', String(weekNumber));
    if (weekDay) params.set('week_day', weekDay);
    const response = await api.get<WinProbModel[]>(`/matchup/probability-matrix?${params}`);
    return response.data;
  },

  async getHeadToHead(
    playerAId: string,
    playerBId: string,
    gameType?: string,
  ): Promise<HeadToHeadResponse> {
    const params = new URLSearchParams();
    if (gameType) params.set('game_type', gameType);
    const query = params.toString() ? `?${params}` : '';
    const response = await api.get<HeadToHeadResponse>(
      `/history/matches/${playerAId}/vs/${playerBId}${query}`
    );
    return response.data;
  },

  // Season (merged division pair) endpoints
  async getSeasonRankings(
    divisionId: string,
    groupByTeam: boolean = false,
  ): Promise<SeasonRankingsResponse> {
    const params = new URLSearchParams({
      group_by_team: groupByTeam.toString(),
    });
    const response = await api.get<SeasonRankingsResponse>(
      `/seasons/${divisionId}/rankings?${params}`
    );
    return response.data;
  },

  async getSeasonInfo(divisionId: string): Promise<SeasonInfo> {
    const response = await api.get<SeasonInfo>(`/seasons/${divisionId}/info`);
    return response.data;
  },
};

// Query keys for React Query
export const queryKeys = {
  health: ['health'] as const,
  divisionRankings: (divisionId: string, groupByTeam: boolean) =>
    ['division', 'rankings', divisionId, groupByTeam] as const,
  divisions: ['divisions'] as const,
  divisionPeriodStats: (divisionId: string) =>
    ['division', 'period-stats', divisionId] as const,
  csrTimeline: (playerId: string, gameType: string, days?: number, divisionId?: string) =>
    ['csr', 'timeline', playerId, gameType, days, divisionId] as const,
  schedule: (divisionId: string) =>
    ['schedule', divisionId] as const,
  sessionScorecard: (divisionId: string, week: number) =>
    ['session-scorecard', divisionId, week] as const,
  compSheet: (divisionId: string, homeTeamId: string, awayTeamId: string) =>
    ['comp-sheet', divisionId, homeTeamId, awayTeamId] as const,
  headToHead: (playerAId: string, playerBId: string, gameType?: string) =>
    ['head-to-head', playerAId, playerBId, gameType] as const,
  seasonRankings: (divisionId: string, groupByTeam: boolean) =>
    ['season', 'rankings', divisionId, groupByTeam] as const,
  seasonInfo: (divisionId: string) =>
    ['season', 'info', divisionId] as const,
};

export default api;